// Tree designs (owner 2026-11-01: 'more rounded, higher polygon, smoother', then 'give me a few other designs… I don't
// like the way the leaves look'). Four original cel designs sharing one interface; props.ts instances the chosen one.
// Unit scale: ~3.6 m tall before TREE_K. Render-only.
import * as THREE from 'three/webgpu';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { attribute, color, float, mix, mx_noise_float, positionGeometry, positionWorld, smoothstep, texture, uv, vec3 } from 'three/tsl';
// owner 2026-11-02: a fallen tree must look exactly like the standing one. The painted tree's mottling now samples the
// GEOMETRY position (fixed to the model) instead of world space, so it doesn't shift when the tree topples or moves.
// Geometry units are ~1/2.4 of world metres, so the frequencies are scaled up to keep the same grain.
const GP = positionGeometry;
import { RenderStyle, toonMaterial } from './toon';

export type TreeStyle = 'painted' | 'roundleaf' | 'puff' | 'cluster' | 'acacia' | 'scale';
export const TREE_STYLES: Record<TreeStyle, string> = {
  painted: 'E · Painted Canopy: hand-painted leaf clusters, sunlit top / violet shade, split trunk',
  roundleaf: 'F · Round-Leaf Toon: clustered round leaves, hard teal cel tones, dark split trunk',
  puff: 'A · Cloud Puff: smooth round cel canopy, no separate leaves',
  cluster: 'B · Leaf Clusters: overlapping rounded leaf clumps',
  acacia: 'C · Canyon Acacia: flat umbrella layers on a forked trunk',
  scale: 'D · Shingled Leaves: soft oval leaves lying flat on round clumps',
};
export interface TreePart { geo: THREE.BufferGeometry; mat: THREE.Material; outline: number }

let seedN = 1;
const rnd = () => { seedN = (seedN * 16807) % 2147483647; return seedN / 2147483647; };

export function blob(r: number, x: number, y: number, z: number, seed: number, sx = 1, sy = 1, sz = 1, lumpK = 0.08, detail = 3): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(r, detail); g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4), pos = w.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3(), n = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i); n.copy(v).normalize();
    v.multiplyScalar(1 + lumpK * Math.sin(n.x * 5.1 + seed) * Math.sin(n.y * 4.3 + seed * 2) * Math.sin(n.z * 4.7 + seed * 3));
    if (v.y < -r * 0.5) v.y = -r * 0.5 + (v.y + r * 0.5) * 0.3; // flat-ish underside
    pos.setXYZ(i, v.x * sx + x, v.y * sy + y, v.z * sz + z);
  }
  w.computeVertexNormals(); return w;
}
export function trunkGeo(h = 2.2, r0 = 0.26, r1 = 0.15, limbs: [number, number, number, number, number][] = []): THREE.BufferGeometry {
  const t = new THREE.CylinderGeometry(r1, r0, h, 14, 6).translate(0, h / 2, 0);
  const pos = t.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) { const y = pos.getY(i); const k = y < 0.35 ? 1 + (0.35 - y) * 0.9 : 1; pos.setX(i, pos.getX(i) * k); pos.setZ(i, pos.getZ(i) * k); }
  const parts: THREE.BufferGeometry[] = [t];
  for (const [len, rad, rz, rx, y] of limbs) parts.push(new THREE.CylinderGeometry(rad * 0.6, rad, len, 10).translate(0, len / 2, 0).rotateZ(rz).rotateX(rx).translate(0, y, 0));
  const out = mergeGeometries(parts.map((g) => { g.deleteAttribute('uv'); return g; }), false)!; out.computeVertexNormals(); return out;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const leafTone = (a: number, b: number, f = 1.3, off = 0): any => mix(color(a), color(b), smoothstep(0.4, 0.6, mx_noise_float(positionWorld.mul(f).add(off)).mul(0.5).add(0.5)));

/** painted leaf-cluster card: ~40 pointed leaves in a ragged round bunch. R = coverage, G = per-leaf value (light variation). */
let leafTex: THREE.Texture | null = null;
export function paintedLeafTexture(): THREE.Texture {
  if (leafTex) return leafTex;
  const S = 256, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
  let sd = 3; const r = () => { sd = (sd * 16807) % 2147483647; return sd / 2147483647; };
  for (let i = 0; i < 46; i++) {
    const a = r() * 6.283, d = Math.sqrt(r()) * S * 0.33, x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d;
    const len = S * (0.09 + r() * 0.07), wid = len * (0.38 + r() * 0.15), rot = a + (r() - 0.5) * 1.6;
    const v = Math.round(110 + r() * 145);
    g.save(); g.translate(x, y); g.rotate(rot); g.fillStyle = `rgb(255,${v},0)`;
    g.beginPath(); g.moveTo(-len * 0.5, 0); g.quadraticCurveTo(0, -wid, len * 0.55, 0); g.quadraticCurveTo(0, wid, -len * 0.5, 0); g.fill();
    // a couple of serration nicks so silhouettes read as leaves, not blobs
    g.globalCompositeOperation = 'destination-out'; g.beginPath(); g.arc(len * 0.1, -wid * 0.75, wid * 0.22, 0, 6.283); g.fill(); g.globalCompositeOperation = 'source-over';
    g.restore();
  }
  leafTex = new THREE.CanvasTexture(c); leafTex.colorSpace = THREE.NoColorSpace; leafTex.anisotropy = 4; return leafTex;
}

let discTex: THREE.Texture | null = null;
/** F: overlapping round leaves (discs) in a ragged bunch; G = per-disc value */
function roundLeafTexture(): THREE.Texture {
  if (discTex) return discTex;
  const S = 256, c = document.createElement('canvas'); c.width = c.height = S; const g = c.getContext('2d')!;
  let sd = 11; const r = () => { sd = (sd * 16807) % 2147483647; return sd / 2147483647; };
  for (let i = 0; i < 34; i++) {
    const a = r() * 6.283, d = Math.sqrt(r()) * S * 0.32, x = S / 2 + Math.cos(a) * d, y = S / 2 + Math.sin(a) * d, rad = S * (0.05 + r() * 0.05);
    g.fillStyle = `rgb(255,${Math.round(120 + r() * 135)},0)`; g.beginPath(); g.arc(x, y, rad, 0, 6.283); g.fill();
  }
  discTex = new THREE.CanvasTexture(c); discTex.colorSpace = THREE.NoColorSpace; discTex.anisotropy = 4; return discTex;
}

/** E · painted canopy: leaf-cluster cards spread over 3–5 canopy lobes. Each card's normal is the lobe's outward
 *  direction, so shading reads as soft round masses; colour bands come from that normal's height (sunlit top →
 *  green → blue-violet underside), which stays correct for any instance spin about Y. No ink outline (painterly). */
function paintedTree(rs: RenderStyle, round = false): TreePart[] {
  const lobes: [number, number, number, number][] = [[1.3, 0, 2.95, 0], [1.05, 0.95, 2.6, 0.3], [1.0, -0.92, 2.65, -0.25], [0.98, 0.15, 2.55, -0.95], [0.95, -0.1, 2.6, 0.95], [0.9, 0.2, 3.75, 0.1]];
  const card = new THREE.PlaneGeometry(0.95, 0.95); card.deleteAttribute('normal');
  const parts: THREE.BufferGeometry[] = []; const n = new THREE.Vector3(), q = new THREE.Quaternion(), m = new THREE.Matrix4(), Z = new THREE.Vector3(0, 0, 1);
  for (const [r, x, y, z] of lobes) {
    const count = Math.round(70 * r * r);
    for (let i = 0; i < count; i++) {
      n.set(rnd() * 2 - 1, rnd() * 2 - 1, rnd() * 2 - 1); if (n.lengthSq() > 1) { i--; continue; } n.normalize();
      const depth = 0.72 + rnd() * 0.36;
      q.setFromUnitVectors(Z, n).multiply(new THREE.Quaternion().setFromAxisAngle(Z, rnd() * 6.283));
      const sc = 0.75 + rnd() * 0.5;
      m.compose(new THREE.Vector3(x + n.x * r * depth, y + n.y * r * depth * 0.9, z + n.z * r * depth), q, new THREE.Vector3(sc, sc, sc));
      const c = card.clone().applyMatrix4(m);
      const cnt = c.attributes.position.count, nn = new Float32Array(cnt * 3);
      for (let k = 0; k < cnt; k++) { nn[k * 3] = n.x; nn[k * 3 + 1] = n.y * 0.85 + (depth - 0.8) * 0.4; nn[k * 3 + 2] = n.z; }
      c.setAttribute('normal', new THREE.BufferAttribute(nn, 3));
      parts.push(c);
    }
  }
  const leaves = mergeGeometries(parts, false)!;
  // solid inner core per lobe (same shading, no alpha) so the canopy never looks see-through
  const cores = mergeGeometries(lobes.map(([r, x, y, z], i) => blob(r * 0.78, x, y, z, i * 3.1, 1, 0.92, 1, 0.07, 3)), false)!;
  const lm = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, alphaTest: 0.5 });
  const tx = texture(round ? roundLeafTexture() : paintedLeafTexture(), uv());
  const ny = attribute('normal', 'vec3').y;
  const k = ny.mul(0.5).add(0.5).add(mx_noise_float(GP.mul(2.2)).mul(0.12)).add(tx.g.sub(0.7).mul(0.25));
  let col;
  if (round) { // hard cel: deep shade / body / lit, plus rare white glints
    col = mix(color(0x0f5a3e), color(0x1f8a5e), smoothstep(0.47, 0.49, k));
    col = mix(col, color(0x52b48a), smoothstep(0.66, 0.68, k));
    col = mix(col, color(0xeafff4), smoothstep(0.985, 0.99, k.add(tx.g.mul(0.06))).mul(0.85));
  } else {
    col = mix(color(0x23284a), color(0x17452a), smoothstep(0.22, 0.3, k));       // violet shade → deep green
    col = mix(col, color(0x2b6129), smoothstep(0.46, 0.52, k));                        // mid green
    col = mix(col, color(0x4f8238), smoothstep(0.64, 0.7, k));                         // sunlit
    col = mix(col, color(0x86a854), smoothstep(0.82, 0.88, k));                        // warm highlight
  }
  lm.colorNode = col; lm.opacityNode = tx.r;
  const cm = new THREE.MeshBasicNodeMaterial();
  const kc = attribute('normal', 'vec3').y.mul(0.5).add(0.5).mul(0.8).add(mx_noise_float(GP.mul(2.2)).mul(0.12)); // core sits a band darker
  cm.colorNode = round ? mix(color(0x0f5a3e), color(0x1f8a5e), smoothstep(0.47, 0.49, kc))
    : mix(mix(color(0x23284a), color(0x17452a), smoothstep(0.22, 0.3, kc)), color(0x2b6129), smoothstep(0.5, 0.56, kc));
  void float; void rs;
  // slim trunk splitting into four limbs that reach into the lobes
  const trunk = trunkGeo(1.5, 0.17, 0.12, [[1.15, 0.08, -0.5, 0.1, 1.35], [1.1, 0.075, 0.48, -0.15, 1.4], [1.0, 0.07, 0.1, 0.55, 1.42], [1.0, 0.065, -0.05, -0.55, 1.45]]);
  if (round) { const dark = rs === 'cel' ? toonMaterial({ color: 0x2a1812 }) : new THREE.MeshStandardNodeMaterial({ color: 0x2a1812, roughness: 0.9 }); return [{ geo: trunk, mat: dark, outline: 0.02 }, { geo: cores, mat: cm, outline: 0 }, { geo: leaves, mat: lm, outline: 0 }]; }
  const bark = rs === 'cel' ? toonMaterial({ color: 0xc69568 }) : new THREE.MeshStandardNodeMaterial({ color: 0xc69568, roughness: 0.9 });
  (bark as THREE.NodeMaterial).colorNode = mix(color(0x5e3e28), color(0x9a7450), smoothstep(0.3, 0.7, mx_noise_float(vec3(GP.x.mul(32), GP.y.mul(2.8), GP.z.mul(32))).mul(0.5).add(0.5)));
  return [{ geo: trunk, mat: bark, outline: 0.025 }, { geo: cores, mat: cm, outline: 0 }, { geo: leaves, mat: lm, outline: 0 }];
}

export function buildTree(style: TreeStyle, rs: RenderStyle): TreePart[] {
  seedN = 7;
  if (style === 'painted') return paintedTree(rs);
  if (style === 'roundleaf') return paintedTree(rs, true);
  const mat = (c: number) => rs === 'cel' ? toonMaterial({ color: c }) : new THREE.MeshStandardNodeMaterial({ color: c, roughness: 0.9 });
  const bark = mat(0x7a5236);
  (bark as THREE.NodeMaterial).colorNode = mix(color(0x5e3c26), color(0x8a603e), smoothstep(0.35, 0.65, mx_noise_float(vec3(positionWorld.x.mul(9), positionWorld.y.mul(0.8), positionWorld.z.mul(9))).mul(0.5).add(0.5)));
  const leafM = (a: number, b: number, f = 1.3, off = 0) => { const m = mat(a); (m as THREE.NodeMaterial).colorNode = leafTone(a, b, f, off); return m; };

  if (style === 'puff') {
    const C: [number, number, number, number][] = [[1.1, 0, 2.6, 0], [0.85, 0.66, 2.25, 0.2], [0.82, -0.62, 2.28, -0.25], [0.75, 0.1, 2.2, -0.7], [0.72, -0.1, 2.3, 0.7], [0.78, 0.05, 3.3, 0.05]];
    const canopy = mergeGeometries(C.map(([r, x, y, z], k) => blob(r, x, y, z, k * 1.7, 1, 1, 1, 0.05)), false)!;
    return [{ geo: trunkGeo(2.3, 0.26, 0.15, [[1, 0.1, -0.7, 0, 1.6]]), mat: bark, outline: 0.04 }, { geo: canopy, mat: leafM(0x3c9a3a, 0x6cc24e, 0.9), outline: 0.05 }];
  }
  if (style === 'cluster') {
    // ~22 medium clumps (slightly flattened) arranged over a dome, each its own soft bulge: reads as bunches of leaves
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 22; i++) {
      const th = rnd() * 6.283, ph = Math.acos(1 - rnd() * 1.25), R = 1.15;
      const x = Math.sin(ph) * Math.cos(th) * R, z = Math.sin(ph) * Math.sin(th) * R, y = 2.45 + Math.cos(ph) * R * 0.85;
      const r = 0.42 + rnd() * 0.18;
      parts.push(blob(r, x, y, z, i * 2.3, 1.05, 0.78, 1.05, 0.06, 2));
    }
    parts.push(blob(1.05, 0, 2.5, 0, 9, 1, 0.85, 1, 0.04, 2)); // core so no gaps show through
    return [{ geo: trunkGeo(2.2, 0.25, 0.14, [[1.1, 0.1, -0.75, 0, 1.5], [1.0, 0.09, 0.7, 0.3, 1.7]]), mat: bark, outline: 0.04 },
      { geo: mergeGeometries(parts, false)!, mat: leafM(0x2f8a34, 0x7ccc58, 1.6, 3), outline: 0.045 }];
  }
  if (style === 'acacia') {
    // forked trunk, two/three wide flat canopy pads (savanna umbrella) — echoes the canyon setting
    const trunk = trunkGeo(1.7, 0.24, 0.13, [[1.6, 0.11, -0.55, 0, 1.4], [1.5, 0.1, 0.6, 0.25, 1.45], [1.2, 0.08, 0.15, -0.6, 1.5]]);
    const pads = [
      blob(1.5, 0.75, 2.95, 0.1, 1, 1.25, 0.32, 1.1, 0.06), blob(1.25, -0.85, 2.85, 0.35, 2, 1.2, 0.3, 1.05, 0.06),
      blob(1.1, 0.0, 3.35, -0.55, 3, 1.25, 0.3, 1.1, 0.06),
    ];
    return [{ geo: trunk, mat: bark, outline: 0.04 }, { geo: mergeGeometries(pads, false)!, mat: leafM(0x4d9a38, 0x8ccc5a, 1.1, 5), outline: 0.05 }];
  }
  // 'scale': round clumps covered by flat, soft oval leaves lying along the surface (overlapping like shingles)
  const C: [number, number, number, number][] = [[1.0, 0, 2.6, 0], [0.8, 0.62, 2.25, 0.2], [0.78, -0.58, 2.28, -0.25], [0.7, 0.1, 2.2, -0.66], [0.7, -0.08, 2.3, 0.66], [0.68, 0.05, 3.2, 0.05]];
  const canopy = mergeGeometries(C.map(([r, x, y, z], k) => blob(r, x, y, z, k * 1.7, 1, 1, 1, 0.04)), false)!;
  const oval = new THREE.CircleGeometry(0.2, 10); oval.scale(0.75, 1.15, 1); oval.deleteAttribute('uv');
  const leaves: THREE.BufferGeometry[] = []; const n = new THREE.Vector3(), q = new THREE.Quaternion(), m = new THREE.Matrix4(), Z = new THREE.Vector3(0, 0, 1);
  for (let i = 0; i < 170; i++) {
    const [r, x, y, z] = C[Math.floor(rnd() * C.length)];
    n.set(rnd() * 2 - 1, rnd() * 1.5 - 0.35, rnd() * 2 - 1).normalize();
    // lie nearly flat on the surface, tipped slightly down (a leaf hangs), random spin about the normal
    q.setFromUnitVectors(Z, n); q.multiply(new THREE.Quaternion().setFromAxisAngle(Z, rnd() * 6.28)).multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), 0.35));
    const sc = 0.85 + rnd() * 0.5;
    m.compose(new THREE.Vector3(x + n.x * r * 1.0, y + n.y * r * 1.0, z + n.z * r * 1.0), q, new THREE.Vector3(sc, sc, sc));
    leaves.push(oval.clone().applyMatrix4(m));
  }
  const lg = mergeGeometries(leaves, false)!; lg.computeVertexNormals();
  const lm = leafM(0x4aa43e, 0x8fd862, 2.4, 8); lm.side = THREE.DoubleSide;
  return [{ geo: trunkGeo(2.2, 0.25, 0.14, [[1, 0.1, -0.7, 0, 1.6]]), mat: bark, outline: 0.04 },
    { geo: canopy, mat: leafM(0x2c7d2e, 0x4fa843, 1.0, 2), outline: 0.05 }, { geo: lg, mat: lm, outline: 0 }];
}
